import { useEffect, useState } from "react";
import { Button, TextField, Typography } from "@mui/material";
import { Controller, ControllerRenderProps, useForm } from "react-hook-form";
import { textFieldStyles, submitButtonStyles, backButtonStyles } from "./theme";
import { useCreateUser } from "@src/entities/login/model/hooks/useCreateUser/useCreateUser";
import { errorMessageForCreateUserRequest } from "./constants";

import styles from "./styles.module.scss";
import { reachGoal } from "@src/shared/lib/analytics/yandex/reachGoal";
import { YM_GOALS } from "@src/shared/lib/analytics/yandex/constants";
interface RegistrationStep2Props {
  login: string;
  code: string;
}
interface Props {
  login: string;
  setStep: (step: number) => void;
  setRegistrationData: (data: any) => void;
}

export const RegistrationStep2 = ({
  login,
  setStep,
  setRegistrationData,
}: Props) => {
  const {
    mutate: createUser,
    error: requestError,
    isPending,
  } = useCreateUser();
  const [countRequestErrors, setCountRequestErrors] = useState<number>(0);

  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
    setValue,
  } = useForm<RegistrationStep2Props>({
    defaultValues: {
      login: login,
      code: "",
    },
    mode: "onTouched",
  });

  useEffect(() => {
    if (requestError) {
      setValue("code", "");
    }
    if (countRequestErrors === 3) {
      setStep(1);
    }
  }, [requestError, countRequestErrors]);

  const onSubmit = (data: RegistrationStep2Props) => {
    createUser(
      { login: data.login, code: data.code },
      {
        onSuccess: () => {
          reachGoal(YM_GOALS.REGISTRATION_STEP(2));
          setRegistrationData((prev: any) => ({ ...prev, code: data.code }));
          setStep(3);
        },
        onError: () => {
          setCountRequestErrors((state) => state + 1);
        },
      }
    );
  };

  const isCodeErrors = !!requestError || !!errors.code;
  const codeHelperText = requestError
    ? errorMessageForCreateUserRequest
    : errors.code?.message;

  return (
    <>
      <div className={styles.formTitleContainer}>
        <Typography className={styles.formTitle}>Регистрация</Typography>
        <Typography className={styles.formTitle2}>Шаг 2</Typography>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className={styles.form}>
        <Controller
          name="login"
          control={control}
          render={({
            field,
          }: {
            field: ControllerRenderProps<RegistrationStep2Props, "login">;
          }) => (
            <TextField
              {...field}
              label="Почта"
              variant="outlined"
              type="email"
              fullWidth
              disabled
              error={!!errors.login}
              helperText={errors.login?.message}
              className={styles.input}
              InputProps={{
                style: {
                  borderRadius: 24,
                  color: "#ffffff46",
                },
              }}
              InputLabelProps={{
                style: { color: "#ffffff46" },
              }}
              sx={textFieldStyles}
            />
          )}
        />
        <Controller
          name="code"
          control={control}
          rules={{
            required: "Поле обязательно для заполнения",
            minLength: {
              value: 6,
              message: "Код должен содержать 6 цифр",
            },
            maxLength: {
              value: 6,
              message: "Код должен содержать 6 цифр",
            },
            pattern: {
              value: /^\d+$/,
              message: "Код должен содержать только цифры",
            },
          }}
          render={({
            field,
          }: {
            field: ControllerRenderProps<RegistrationStep2Props, "code">;
          }) => (
            <TextField
              {...field}
              label="Код подтверждения"
              variant="outlined"
              type="text"
              fullWidth
              error={isCodeErrors}
              helperText={codeHelperText}
              className={styles.input}
              InputProps={{
                style: {
                  borderRadius: 24,
                  color: "#fff",
                },
              }}
              InputLabelProps={{
                style: { color: "#ffffff83" },
              }}
              sx={textFieldStyles}
            />
          )}
        />
        <Button
          disabled={!isValid || isPending}
          type="submit"
          variant="contained"
          className={styles.button}
          sx={submitButtonStyles}
        >
          Отправить код подтверждения
        </Button>
      </form>
      <Button variant="text" onClick={() => setStep(1)} sx={backButtonStyles}>
        Вернуться
      </Button>
    </>
  );
};
